import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useAuthContext } from '@/contexts/AuthContext';
import { useNotificacoes } from '@/hooks/useNotificacoes';
import { notificacaoLink, notificacaoTexto, type NotificacaoTipo, type NotificacaoDados } from '@/lib/notificacoes';
import { cn } from '@/lib/utils';

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isConsumer } = useAuthContext();
  const { notificacoes, naoLidas, marcarLidas, marcarTodas } = useNotificacoes();

  const abrir = (id: string, tipo: NotificacaoTipo, dados: NotificacaoDados) => {
    marcarLidas([id]);
    navigate(notificacaoLink(tipo, dados, isConsumer));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={t('notifications.aria')}>
          <Bell className="h-5 w-5" />
          {naoLidas > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold flex items-center justify-center">
              {naoLidas > 9 ? '9+' : naoLidas}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto p-0">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="font-semibold text-sm">{t('notifications.title')}</span>
          {naoLidas > 0 && (
            <button type="button" className="text-xs text-primary hover:underline" onClick={() => marcarTodas()}>
              {t('notifications.markAllRead')}
            </button>
          )}
        </div>
        {notificacoes.length === 0 ? (
          <p className="px-3 py-6 text-sm text-muted-foreground text-center">{t('notifications.empty')}</p>
        ) : (
          notificacoes.map((n) => {
            const { key, vars } = notificacaoTexto(n.tipo, n.dados);
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => abrir(n.id, n.tipo, n.dados)}
                className={cn(
                  'w-full text-left px-3 py-2 text-sm hover:bg-muted/50 border-b border-border/50',
                  !n.lida && 'bg-primary/5 font-medium',
                )}
              >
                {t(key, vars)}
              </button>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
